import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  AdminAccessEditorFields,
  AdminRelatedLinks,
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  createYowanAdminMemberAccess,
  fetchYowanAdminMemberOptions,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const getAccessCreateErrorMessage = (error, content) => {
  const responseData = error?.response?.data || {};

  if (error?.response?.status === 409 || responseData.reason === 'duplicate_member_access') {
    return content.form.duplicateMessage;
  }

  return content.form.errorMessage;
};

const AdminMemberAccessCreatePage = () => {
  const content = yowanPageContent['/admin/access/member/new'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const memberId = searchParams.get('memberId') || '';
  const [submitMessage, setSubmitMessage] = useState('');
  const [memberOptions, setMemberOptions] = useState(content?.form.memberOptions || []);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [optionLoadError, setOptionLoadError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...content?.form.defaults,
      memberId: memberId || content?.form.defaults?.memberId || '',
    },
  });

  useEffect(() => {
    if (!content) {
      return undefined;
    }

    let isMounted = true;

    const loadMemberOptions = async () => {
      setIsLoadingOptions(true);
      setOptionLoadError('');

      try {
        const options = await fetchYowanAdminMemberOptions();
        const mergedOptions = [
          { value: '', label: '対象会員を選択' },
          ...options,
        ];

        if (memberId && !mergedOptions.some((option) => option.value === memberId)) {
          mergedOptions.push({ value: memberId, label: '選択済み会員' });
        }

        if (isMounted) {
          setMemberOptions(mergedOptions);
        }
      } catch (error) {
        if (isMounted) {
          setOptionLoadError(content.form.loadErrorMessage);
        }
      } finally {
        if (isMounted) {
          setIsLoadingOptions(false);
        }
      }
    };

    loadMemberOptions();

    return () => {
      isMounted = false;
    };
  }, [content, memberId]);

  if (!content) {
    return null;
  }

  const pageContent = {
    ...content,
    form: {
      ...content.form,
      memberOptions,
    },
  };

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      await createYowanAdminMemberAccess(values);
      setSubmitMessage(content.form.successMessage);
      navigate(appendYowanPreviewQueryIfNeeded('/admin/access/member', preservedDomainSearch));
    } catch (error) {
      setSubmitMessage(getAccessCreateErrorMessage(error, content));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminBlogShell activeNavHref="/admin/access/member" routeKey="/admin/access/member/new">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/admin/access/member', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />
      <AdminRelatedLinks
        links={content.relatedLinks}
        preservedDomainSearch={preservedDomainSearch}
      />

      <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        {isLoadingOptions ? <LoadingSpinner size="sm" /> : null}
        {optionLoadError ? <p className="yowan-admin-form__message">{optionLoadError}</p> : null}
        <AdminAccessEditorFields
          content={pageContent}
          register={register}
          errors={errors}
          memberLocked={Boolean(memberId)}
          lockedMemberId={memberId}
        />

        <div className="yowan-admin-form__actions">
          <button type="submit" className="c-form__submit" disabled={isSubmitting || isLoadingOptions}>
            {isSubmitting ? content.form.pendingMessage : content.form.submitLabel}
          </button>
        </div>

        {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}
      </form>
    </AdminBlogShell>
  );
};

export default AdminMemberAccessCreatePage;
